import router from "@/router";
import { useToast } from "vue-toastification";
import DeviceUtility from "@/helpers/device";
import { calculateTimeout } from "@/helpers/utilities";

/**
 * Opens an in-app route while keeping the user in their current context — a
 * browser tab stays in the browser, the PWA stays in the PWA (LSP-16376).
 *
 * Inside an installed PWA there are no browser tabs and window.open(_, "_blank")
 * hands the route off to the OS browser, so we navigate within the PWA window
 * instead. In a regular browser we pre-open a blank window and then navigate it:
 * because no in-scope URL is present at open time, the installed PWA cannot
 * capture the link, so it opens as a new tab in the current browser (mirrors the
 * pre-open shape in ActionStartRecording.startInstantMeeting()). If that pre-open
 * is blocked we keep the user on their current screen and warn them — navigating
 * away would unload the in-progress dialog the new tab exists to preserve, which
 * matches the popup-blocked handling in ActionStartRecording.startInstantMeeting().
 */
export function openInternalRoute(path: string): void {
	if (DeviceUtility.isPwaInstalled) {
		void router.push(path);
		return;
	}

	const url = new URL(path, window.location.origin).href;
	const opened = window.open("", "_blank");

	if (opened) {
		opened.location.href = url;
		return;
	}

	const message = "We couldn't open a new tab. Please allow pop-ups for this site, then try again.";
	useToast().error(message, { timeout: calculateTimeout(message) });
}
